import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useConfirm from "../../../hooks/useConfirm";
import useToast from "../../../hooks/useToast";
import { SousDomaineService } from "../../../services/candidature/SousDomaineService";
import SousDomaineDialogForm from "./domaine_sousdomaine/SousDomaineDialogForm";

const SousDomaineForm = ({ domaine, onDelete }) => {
  const [sousDomaine, setSousDomaine] = React.useState(
    domaine.sous_domaines || []
  );
  const [openFormDialog, setOpenFormDialog] = React.useState(false);
  const [selectedIdSousDomaine, setSelectedIdSousDomaine] = React.useState(null);
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const { showToast, Toast } = useToast();
  const [loading, setLoading] = React.useState(false);

  const handleFechSousDomaine = () => {
    setLoading(true);
    SousDomaineService.lister({ id__domaine: domaine.id_domaine })
      .then((res) => {
        if (res && res.data) {
          setSousDomaine(res.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteSousDomaine = (idSousDomaine) => {
    showToast(TYPE_NOTIFICATION.INFO, "Suppression en cours...");
    SousDomaineService.delete({ id_sous_domaine: idSousDomaine })
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          setSousDomaine((prevSousDomaines) =>
            prevSousDomaines.filter((sd) => sd.id_sous_domaine !== idSousDomaine)
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleConfirmDeleteSousDomaine = (idSousDomaine) => {
    openConfirmDialog(
      "Confirmer la suppression",
      "Êtes-vous sûr de vouloir supprimer ce sous-domaine ?",
      () => {
        handleDeleteSousDomaine(idSousDomaine);
      }
    );
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(domaine.id_domaine_type);
    }
  };

  const handleOpenForm = (idSousDomaine = null) => {
    setSelectedIdSousDomaine(idSousDomaine);
    setOpenFormDialog(true);
  };

  const handleCloseForm = () => {
    setOpenFormDialog(false);
    setSelectedIdSousDomaine(null);
    handleFechSousDomaine();
  };

  return (
    <>
      <Toast />
      <DialogComponent />
      <SousDomaineDialogForm
        open={openFormDialog}
        id_domaine={domaine.id_domaine}
        idSousDomaine={selectedIdSousDomaine}
        onFormClose={handleCloseForm}
      />
      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: "8px !important",
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 64,
            backgroundColor: "#FFFFFF",
            "& .MuiAccordionSummary-content": { alignItems: "center" },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ width: "100%", pr: 1 }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                  backgroundColor: "#EAF2FF",
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" fontWeight={800}>
                  {String(domaine?.name || "D").slice(0, 1).toUpperCase()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" fontWeight={800}>
                  {domaine?.name || "Domaine non spécifié"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sousDomaine.length} sous-domaine{sousDomaine.length > 1 ? "s" : ""}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Actualiser les sous-domaines">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleFechSousDomaine();
                    }}
                    disabled={loading}
                  >
                    <RefreshOutlined />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Ajouter un sous-domaine">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenForm(null);
                  }}
                >
                  <AddOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Supprimer le domaine">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete();
                  }}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, backgroundColor: "#F8FAFC" }}>
          {loading && (
            <Grid display="flex" justifyContent="center" alignItems="center" minHeight="16vh">
              <CircularProgress size="2.5rem" />
            </Grid>
          )}

          {!loading && sousDomaine.length === 0 && (
            <Alert
              severity="warning"
              variant="outlined"
              action={
                <Button
                  color="primary"
                  size="small"
                  startIcon={<AddOutlined />}
                  onClick={() => handleOpenForm(null)}
                >
                  Ajouter
                </Button>
              }
              sx={{ borderRadius: 2, backgroundColor: "#FFFFFF", alignItems: "center" }}
            >
              Aucun sous-domaine assigné.
            </Alert>
          )}

          {!loading && sousDomaine.length > 0 && (
            <Grid container spacing={1}>
              {sousDomaine.map((sd) => (
                <Grid item /* xs={12} sm={6} md={4} */ max-width="100%" key={sd.id_sous_domaine}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.25, borderRadius: 2, backgroundColor: "#FFFFFF" }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Chip
                        label={sd.name || "Sous-domaine non spécifié"}
                        variant="outlined"
                        sx={{ maxWidth: "100%", fontWeight: 600 }}
                      />
                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenForm(sd.id_sous_domaine)}
                          >
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleConfirmDeleteSousDomaine(sd.id_sous_domaine)
                            }
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default SousDomaineForm;
